import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Done from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { createColumnHelper } from '@tanstack/react-table';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';

import AssetDuration from '../../../components/asset-duration/asset-duration';
import FuelAutoComplete from '../../../components/fuel-autocomplete';
import FuelTable from '../../../components/fuel-table/FuelTable';
import FuelTags from '../../../components/fuel-tags';
import FuelAssetThumbnail from '../../../components/FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../../../components/FuelAssetTitle/FuelAssetTitle';
import MetadataContent from '../../../components/MetadataContent';
import type { Category, Item } from '../../../models';
import { CustomAttributes } from '../../../models/customAttributes';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { assetListingSelector } from '../../../store/slices/asset-listing/asset-listing';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { dateUtils } from '../../../utils/dateUtils';
import itemUtils from '../../../utils/itemUtils';
import AssetDialogHeader from '../asset-details-modal/AssetDialogHeader';

interface IPros {
  open: boolean;
  onClose: () => void;
  onSave: (selectedCategories, selectedTags) => void;
}
const columnHelper = createColumnHelper<any>();

const EditMultipleModal = ({ open, onClose, onSave }: IPros) => {
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const { ouData, categories } = useSelector(applicationDataSelector);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [allowMultiCategorySelect, setAllowMultiCategorySelect] = useState(true);
  const { assetListingState } = useSelector(assetListingSelector);
  const { editMultipleForm } = assetListingState;
  const [assets, setAssets] = useState<Item[]>(editMultipleForm.assets);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTags, setSelectedTags] = useState(editMultipleForm.initialCommonModel.selectedTags);
  const [rightOpen, setRightOpen] = React.useState(editMultipleForm.mode === 'edit');
  const [tagState, setTagState] = useState(CustomAttributes.Create());
  const { loading } = useProgress();
  //   const { isSaving } = editMultipleForm;
  const handleClose = () => {
    // setOpen(false);
    onClose();
  };
  const tagStateRef = useRef(CustomAttributes.Create());

  const Thumbnail = useCallback(
    (cell) => <FuelAssetThumbnail item={cell.row.original} permissions={permissions} />,
    [permissions]
  );

  const columns = [
    columnHelper.accessor('isDone', {
      header: () => '',
      width: 60,
      footer: (props) => props.column.id,
      sortable: false,
      cell: (props) => {
        if (!props.row.original.isDone && !props.row.original.hasWarning) return <></>;
        if (props.row.original.isDone && !props.row.original.hasWarning)
          return <Done sx={{ color: '#78b13f', fontSize: '48px', marginLeft: 1 }} />;
        if (!props.row.original.isDone && props.row.original.hasWarning)
          return <PriorityHighIcon sx={{ color: '#fd3', fontSize: '48px', marginLeft: 1 }} />;
      },
    }),
    columnHelper.accessor('thumbnailSrc', {
      header: '',
      cell: Thumbnail,
    }),
    columnHelper.accessor('title', {
      align: 'left',
      header: () => 'Title',
      width: 200,
      sortable: true,
      footer: (props) => props.column.id,
      cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} />,
    }),
    columnHelper.accessor('publicationDate', {
      header: `Publication (${ouData.timezoneAbbreviation}) `,
      footer: (props) => props.column.id,
      width: 120,
      sortable: true,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('categoryString', {
      header: 'Categories',
      footer: (props) => props.column.id,
      width: 100,
      sortable: true,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    columnHelper.accessor('tagString', {
      header: 'Tags',
      footer: (props) => props.column.id,
      sortable: true,
      width: 100,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    // columnHelper.accessor('data.accessSettings.effectiveAccessLevel', {
    //   header: () => `Access`,
    //   footer: (props) => props.column.id,
    //   width: 130,
    //   sortable: false,
    //   cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
    // }),
    columnHelper.accessor('embargoDate', {
      header: () => `Embargo (${ouData.timezoneAbbreviation})`,
      footer: (props) => props.column.id,
      width: 130,
      sortable: true,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('expireDate', {
      header: `Expiry (${ouData.timezoneAbbreviation})`,
      footer: (props) => props.column.id,
      width: 130,
      sortable: true,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('durationString', {
      header: 'Duration',
      footer: (props) => props.column.id,
      width: 75,
      sortable: true,
      cell: (cell) => <AssetDuration item={cell.row.original} />,
    }),
  ];

  const paginationChange = () => {};

  const handleTagsChange = (value) => {
    setSelectedTags(value);
    refreshLongFormFields(value);
  };
  const getCategories = (ids: string[]) => {
    const items = ouData.categories.filter(function (item) {
      return ids.indexOf(item.id) >= 0;
    });
    return items;
  };

  const expandRightSide = (val) => {
    // console.log('val', val);
    setRightOpen(!rightOpen);
  };

  useEffect(() => {
    // console.log('editMultipleForm--', editMultipleForm);
    loadCategoriesandTags();
    setAssets(editMultipleForm.assets);
  }, [editMultipleForm, editMultipleForm.assets]);

  const loadCategoriesandTags = () => {
    const currentModelCategories = editMultipleForm.currentCommonModel.selectedCategories;
    const currentModelTags = editMultipleForm.currentCommonModel.selectedTags;
    setSelectedTags(currentModelTags);
    refreshLongFormFields(currentModelTags);

    // Set allowMultipleCategories
    const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
    const itemCategories = getCategories(currentModelCategories);

    setSelectedCategories(itemCategories);

    if (allowMultipleCategories === true || itemCategories?.length > 1) {
      setAllowMultiCategorySelect(true);
    } else {
      setAllowMultiCategorySelect(false);
    }

    // Set categories
    const myFilteredCategories = itemUtils.getFilteredCategories(ouData.categories, itemCategories);
    setFilteredCategories(myFilteredCategories);
  };

  const refreshLongFormFields = (tags) => {
    const myState = itemUtils.extractCustomAttributesFromTags(tags, tagStateRef.current);
    // console.log('refreshLongFormFields', tags);
    tagStateRef.current = myState;
    setTagState(myState);
  };

  const onInputChange = (event) => {
    // console.log('onInputChange', event);
    if (event.key === ',' || event.key === '\\') {
      event.preventDefault();
      alert('Commas and backslashes not allowed in tag names.', 'warning');
      return;
    }
    const myState = cloneDeep(tagStateRef.current);
    myState[event.target.name] = event.target.value;
    tagStateRef.current = myState;
    setTagState(myState);
  };
  const onSingleTagFieldBlur = (event) => {
    // console.log('onCustomTagFieldBlur2', event);
    const existingTags: string[] = selectedTags;
    const newTags = itemUtils.onSingleTagFieldBlur(event, existingTags);
    // assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    handleTagsChange(newTags);
    refreshLongFormFields(newTags);
  };
  const onMultipleTagFieldBlur = (event) => {
    const existingTags: string[] = selectedTags;
    const newTags = itemUtils.onMultipleTagFieldBlur(event, existingTags);
    // console.log('onCustomTagFieldBlur newTags', newTags);
    handleTagsChange(newTags);
    refreshLongFormFields(newTags);
  };

  useEffect(() => {
    // console.log('assets updated--', assets);
  }, [assets]);
  useEffect(() => {
    // console.log('assets editMultipleForm.assets--', editMultipleForm.assets);
  }, [editMultipleForm.assets]);

  return (
    <div id="edit-multiple-modal">
      <Dialog open={open} fullWidth maxWidth={rightOpen ? 'lg' : 'md'}>
        <Grid container>
          <Grid item xs={rightOpen ? 8 : 12}>
            <AssetDialogHeader
              title={editMultipleForm.mode === 'edit' ? 'Edit Categories & Tags' : 'Delete'}
              onClose={onClose}
              onTitleClick={() => {}}
              rightOpen={rightOpen}
              expandRightSide={expandRightSide}
              showRightExpandIcon={editMultipleForm.mode === 'edit'}
            />
            <DialogContent dividers sx={{ height: '65vh' }}>
              <div className="edit-multiple-fuel-table">
                <FuelTable
                  columnHelper={columnHelper}
                  data={assets}
                  defaultColumns={columns}
                  paginationDetail={editMultipleForm.pagination}
                  paginationChange={paginationChange}
                  showRowSelection={false}
                  loading={false}
                  tableHeight={30}
                  hidePagination
                />
              </div>
              {editMultipleForm.mode === 'edit' && <Divider />}
              {editMultipleForm.mode === 'edit' && (
                <Grid container spacing={2} sx={{ marginTop: 1, marginBottom: 1 }}>
                  <Grid item xs={6}>
                    <FuelAutoComplete
                      id="Common-category-select"
                      items={categories}
                      itemText="name"
                      itemKey="id"
                      labelText="Common Categories"
                      placeHolderText="Select Common Categories"
                      multiSelect={allowMultiCategorySelect}
                      allowEmptyValue
                      labelPaddingTop="-5px"
                      defaultValue={itemUtils.getDefaultSelectedCategories(
                        allowMultiCategorySelect,
                        filteredCategories,
                        selectedCategories
                      )}
                      onChange={(values) => setSelectedCategories(itemUtils.ensureArray(values))}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FuelTags
                      id="fuel-ingest-tag-id"
                      tags={selectedTags}
                      onChange={(value) => handleTagsChange(value)}
                      inputTagWidth={365}
                      labelText="Common Tags"
                    />
                  </Grid>
                </Grid>
              )}
            </DialogContent>
          </Grid>
          {rightOpen && (
            <Grid item xs={4} sx={{ borderLeft: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <Box>
                  {/* <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon />
                      </IconButton> */}
                </Box>
                <IconButton onClick={expandRightSide}>
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <DialogContent dividers sx={{ height: '65vh' }}>
                <MetadataContent
                  tagState={tagState}
                  isEditingDisabled={false}
                  onInputChange={onInputChange}
                  onSingleTagFieldBlur={onSingleTagFieldBlur}
                  onMultipleTagFieldBlur={onMultipleTagFieldBlur}
                />
              </DialogContent>
            </Grid>
          )}
        </Grid>
        <DialogActions>
          <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
            Close
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            onClick={() => onSave(selectedCategories, selectedTags)}
            disabled={loading || (editMultipleForm.mode === 'delete' && editMultipleForm.hasSaved)}
          >
            {editMultipleForm.mode === 'edit' ? 'Apply' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditMultipleModal;
